import { useState, useEffect, useRef, useMemo } from "react";
import { Plus, Trash2, FileText, Download, CheckCircle, Save, Eye, BookOpen, LogOut, UserCircle, RefreshCw, AlertTriangle, Key, Upload, EyeOff, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { mockExam } from "../data/mockData";
import ExamImporter from "./ExamImporter";
import api from "../services/api";

/* ─── Teacher Login Screen ─── */
const TeacherLoginScreen = ({ teachers, onLogin }) => {
  const [u, setU] = useState(""); const [p, setP] = useState(""); const [show, setShow] = useState(false); const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const res = await api.loginTeacher(u, p);
      onLogin(res.data.teacher);
    } catch (e) {
      setErr(e.response?.data?.detail || "ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F5F6] px-4" style={{ fontFamily: "'Noto Sans Thai', sans-serif" }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-3"><BookOpen size={28} color="white" strokeWidth={1.5} /></div>
          <h1 className="text-xl font-semibold text-[#1A1B1F]" style={{ fontFamily: "'Prompt', sans-serif" }}>เข้าสู่ระบบอาจารย์ผู้สอน</h1>
          <p className="text-sm text-[#5E636E]">วิทยาลัยนวัตกรรมสื่อสารสังคม มศว</p>
        </div>
        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">ชื่อผู้ใช้</label>
            <input data-testid="teacher-username-input" value={u} onChange={(e) => { setU(e.target.value); setErr(""); }} placeholder="กรอกชื่อผู้ใช้" className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">รหัสผ่าน</label>
            <div className="relative">
              <input data-testid="teacher-password-input" type={show ? "text" : "password"} value={p} onChange={(e) => { setP(e.target.value); setErr(""); }} placeholder="กรอกรหัสผ่าน" className="w-full border border-gray-300 rounded-md px-4 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>
          {err && <div data-testid="teacher-login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
          <button data-testid="teacher-login-btn" type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-60">
            {loading ? "กำลังเข้าระบบ..." : "เข้าสู่ระบบ"}
          </button>
          <p className="text-xs text-center text-[#8A92A3]">ตัวอย่าง — ชื่อผู้ใช้: <strong>somchai</strong> | รหัสผ่าน: <strong>teacher1234</strong></p>
        </form>
      </div>
    </div>
  );
};

/* ─── Change Password Modal ─── */
const ChangePasswordModal = ({ teacher, onSave, onClose }) => {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [conf, setConf] = useState(""); const [err, setErr] = useState(""); const [ok, setOk] = useState(false);
  const [showCur, setShowCur] = useState(false); const [showNw, setShowNw] = useState(false);
  const handleSave = () => {
    if (cur !== teacher.password) { setErr("รหัสผ่านปัจจุบันไม่ถูกต้อง"); return; }
    if (nw.length < 6) { setErr("รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร"); return; }
    if (nw !== conf) { setErr("รหัสผ่านใหม่และยืนยันรหัสผ่านไม่ตรงกัน"); return; }
    onSave(teacher.id, nw); setOk(true); setTimeout(onClose, 1500);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backdropFilter: "blur(4px)", backgroundColor: "rgba(0,0,0,0.4)", fontFamily: "'Noto Sans Thai', sans-serif" }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4 fade-in-up">
        <h2 className="font-semibold text-[#1A1B1F] flex items-center gap-2" style={{ fontFamily: "'Prompt', sans-serif" }}><Key size={16} strokeWidth={1.5} className="text-[#007CB7]" />เปลี่ยนรหัสผ่าน</h2>
        {ok ? (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-md px-4 py-3 text-sm text-green-700"><CheckCircle size={16} strokeWidth={1.5} />เปลี่ยนรหัสผ่านสำเร็จ</div>
        ) : (
          <>
            <div><label className="block text-xs font-medium text-[#1A1B1F] mb-1">รหัสผ่านปัจจุบัน</label>
              <div className="relative"><input type={showCur ? "text" : "password"} value={cur} onChange={e => { setCur(e.target.value); setErr(""); }} placeholder="รหัสผ่านปัจจุบัน" className="w-full border border-gray-300 rounded-md px-3 py-2 pr-9 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" data-testid="current-password-input" />
                <button type="button" onClick={() => setShowCur(!showCur)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">{showCur ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              </div>
            </div>
            <div><label className="block text-xs font-medium text-[#1A1B1F] mb-1">รหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)</label>
              <div className="relative"><input type={showNw ? "text" : "password"} value={nw} onChange={e => { setNw(e.target.value); setErr(""); }} placeholder="รหัสผ่านใหม่" className="w-full border border-gray-300 rounded-md px-3 py-2 pr-9 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" data-testid="new-password-input" />
                <button type="button" onClick={() => setShowNw(!showNw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">{showNw ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              </div>
            </div>
            <div><label className="block text-xs font-medium text-[#1A1B1F] mb-1">ยืนยันรหัสผ่านใหม่</label>
              <input type="password" value={conf} onChange={e => { setConf(e.target.value); setErr(""); }} placeholder="ยืนยันรหัสผ่านใหม่" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" data-testid="confirm-new-password-input" />
            </div>
            {err && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-md text-sm text-[#5E636E] hover:bg-gray-50">ยกเลิก</button>
              <button onClick={handleSave} data-testid="save-password-btn" className="flex-1 py-2 bg-[#007CB7] hover:bg-[#005F8C] text-white rounded-md text-sm font-semibold transition-colors">บันทึก</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ─── My Course Manager ─── */
const MyCourseManager = ({ courses, setCourses, teacher }) => {
  const [showForm, setShowForm] = useState(false); const [form, setForm] = useState({ id: "", name: "", students: "" }); const [delConfirm, setDelConfirm] = useState(null);
  const myCourses = courses.filter(c => c.teacher === teacher.name);
  const handleAdd = async () => {
    if (!form.id || !form.name) return;
    try {
      const res = await api.addCourse({ id: form.id, name: form.name, teacher: teacher.name, students: parseInt(form.students) || 0, year: "2567", semester: 1, status: "เปิดสอน" });
      setCourses(prev => [...prev, res.data]);
      setShowForm(false); setForm({ id: "", name: "", students: "" });
      toast.success("เพิ่มรายวิชาสำเร็จ");
    } catch (err) {
      toast.error(err.response?.data?.detail || "เพิ่มรายวิชาไม่สำเร็จ");
    }
  };
  const handleDelete = async (id) => {
    try {
      await api.deleteCourse(id);
      setCourses(prev => prev.filter(c => c.id !== id));
      setDelConfirm(null);
      toast.success("ลบรายวิชาสำเร็จ");
    } catch (err) {
      toast.error("ลบไม่สำเร็จ");
    }
  };
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-[#1A1B1F] flex items-center gap-2" style={{ fontFamily: "'Prompt', sans-serif" }}>
          <BookOpen size={15} strokeWidth={1.5} className="text-[#007CB7]" />รายวิชาของฉัน ({myCourses.length} วิชา)
        </h3>
        <button data-testid="add-course-btn" onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors">
          <Plus size={13} strokeWidth={1.5} />เพิ่มวิชา
        </button>
      </div>
      {showForm && (
        <div className="px-5 py-4 border-b border-gray-100 bg-blue-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-[#1A1B1F] mb-1">รหัสวิชา *</label><input value={form.id} onChange={e => setForm(p => ({...p, id: e.target.value}))} placeholder="เช่น SC303" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" /></div>
            <div><label className="block text-xs font-medium text-[#1A1B1F] mb-1">ชื่อรายวิชา *</label><input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="เช่น การวิเคราะห์สื่อ" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" /></div>
            <div><label className="block text-xs font-medium text-[#1A1B1F] mb-1">จำนวนนิสิต</label><input type="number" value={form.students} onChange={e => setForm(p => ({...p, students: e.target.value}))} placeholder="0" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => { setShowForm(false); setForm({ id: "", name: "", students: "" }); }} className="px-4 py-1.5 border border-gray-300 rounded-md text-sm text-[#5E636E] hover:bg-gray-50">ยกเลิก</button>
            <button onClick={handleAdd} disabled={!form.id || !form.name} data-testid="confirm-add-course-btn" className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${form.id && form.name ? 'bg-[#007CB7] text-white hover:bg-[#005F8C]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>บันทึกวิชา</button>
          </div>
        </div>
      )}
      {myCourses.length === 0 ? (
        <div className="px-5 py-8 text-center text-[#8A92A3] text-sm">ยังไม่มีรายวิชา กดปุ่ม "เพิ่มวิชา" เพื่อเพิ่มรายวิชาแรก</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="my-courses-table">
            <thead><tr className="bg-gray-50 text-[#5E636E] text-xs uppercase tracking-wider">
              <th className="text-left px-5 py-3 font-semibold">รหัสวิชา</th><th className="text-left px-5 py-3 font-semibold">ชื่อรายวิชา</th><th className="text-left px-5 py-3 font-semibold">จำนวนนิสิต</th><th className="text-left px-5 py-3 font-semibold">การจัดการ</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {myCourses.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-mono text-xs text-[#007CB7] font-semibold">{c.id}</td>
                  <td className="px-5 py-3 text-[#1A1B1F] font-medium">{c.name}</td>
                  <td className="px-5 py-3 text-[#5E636E]">{c.students} คน</td>
                  <td className="px-5 py-3">
                    {delConfirm === c.id ? (
                      <div className="flex items-center gap-2"><span className="text-xs text-red-600">ยืนยันลบ?</span><button onClick={() => handleDelete(c.id)} className="text-xs text-red-600 font-medium">ยืนยัน</button><button onClick={() => setDelConfirm(null)} className="text-xs text-gray-500">ยกเลิก</button></div>
                    ) : (
                      <button data-testid={`delete-course-${c.id}`} onClick={() => setDelConfirm(c.id)} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors"><Trash2 size={12} strokeWidth={1.5} />ลบ</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ─── Question Card ─── */
const QUESTION_TEMPLATE = () => ({ id: Date.now() + Math.random(), type: "ปรนัย", text: "", score: 3, options: ["", "", "", ""], correctOption: 0 });

const QuestionCard = ({ question, index, onChange, onRemove }) => {
  const updateOption = (i, val) => { const opts = [...question.options]; opts[i] = val; onChange(question.id, "options", opts); };
  return (
    <div data-testid={`question-card-${index}`} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm slide-in space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full bg-[#007CB7] text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">{index + 1}</div>
          <select data-testid={`question-type-${index}`} value={question.type} onChange={(e) => onChange(question.id, "type", e.target.value)} className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none">
            <option value="ปรนัย">ปรนัย (Multiple Choice)</option><option value="อัตนัย">อัตนัย (Essay)</option>
          </select>
          <div className="flex items-center gap-1.5"><label className="text-xs text-[#5E636E]">คะแนน:</label><input data-testid={`question-score-${index}`} type="number" min="1" max="100" value={question.score} onChange={(e) => onChange(question.id, "score", Number(e.target.value))} className="w-14 border border-gray-300 rounded-md px-2 py-1.5 text-sm text-center focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" /></div>
        </div>
        <button data-testid={`remove-question-${index}`} onClick={() => onRemove(question.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50"><Trash2 size={15} strokeWidth={1.5} /></button>
      </div>
      <textarea data-testid={`question-text-${index}`} value={question.text} onChange={(e) => onChange(question.id, "text", e.target.value)} placeholder={`พิมพ์คำถามข้อที่ ${index + 1} ที่นี่...`} rows={2} className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none resize-none" />
      {question.type === "ปรนัย" && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-[#5E636E] uppercase tracking-wide">ตัวเลือก (ทำเครื่องหมายที่คำตอบที่ถูก)</p>
          {question.options.map((opt, i) => (
            <div key={`opt-${question.id}-${i}`} className="flex items-center gap-3">
              <input type="radio" name={`correct-${question.id}`} checked={question.correctOption === i} onChange={() => onChange(question.id, "correctOption", i)} className="w-4 h-4 text-[#007CB7]" />
              <span className="text-sm font-medium text-[#5E636E] w-5">{String.fromCharCode(65 + i)}.</span>
              <input data-testid={`option-${index}-${i}`} type="text" value={opt} onChange={(e) => updateOption(i, e.target.value)} placeholder={`ตัวเลือก ${String.fromCharCode(65 + i)}`} className={`flex-1 border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none ${question.correctOption === i ? "border-green-400 bg-green-50" : "border-gray-300"}`} />
            </div>
          ))}
        </div>
      )}
      {question.type === "อัตนัย" && <div className="bg-purple-50 border border-purple-200 rounded-md px-3 py-2 text-xs text-purple-700">คำถามอัตนัย — ตอบในรูปแบบข้อความ</div>}
    </div>
  );
};

/* ─── Student Detail / Grading Modal ─── */
const StudentDetailModal = ({ submission, examData, onClose, onSave }) => {
  const [essayScores, setEssayScores] = useState(() => ({ ...(submission.essayScores || {}) }));

  // Use the ACTUAL exam's questions — fixes wrong-exam-data bug
  const questions = examData?.questions || mockExam.questions;
  const totalMax = examData?.totalScore || mockExam.totalScore;
  const mcQuestions = useMemo(() => questions.filter(q => q.type === "ปรนัย"), [questions]); // eslint-disable-line react-hooks/exhaustive-deps
  const essayQuestions = useMemo(() => questions.filter(q => q.type === "อัตนัย"), [questions]); // eslint-disable-line react-hooks/exhaustive-deps
  const maxEssayScore = essayQuestions.reduce((s, q) => s + q.score, 0);
  const essayTotal = Object.values(essayScores).reduce((s, v) => s + Number(v || 0), 0);
  const totalScore = (submission.mcScore || 0) + essayTotal;

  // Handle both {id, text, isCorrect} objects AND string options
  const getOptId = (opt, i) => typeof opt === "object" ? opt.id : String.fromCharCode(97 + i);
  const getOptText = (opt) => typeof opt === "object" ? opt.text : opt;
  const getCorrectId = (q) => typeof q.options[0] === "object"
    ? q.options.find(o => o.isCorrect)?.id
    : String.fromCharCode(97 + (q.correctOption ?? 0));

  const getOptionStyle = (optId, correctId, chosen) => {
    if (optId === correctId && optId === chosen) return "border-green-400 bg-green-50";
    if (optId === correctId) return "border-green-300 bg-green-50";
    if (optId === chosen) return "border-red-300 bg-red-50";
    return "border-gray-200 bg-white";
  };

  const handleSave = () => { onSave(submission.id, essayScores, totalScore); onClose(); };

  const allEssayScored = essayQuestions.every((_, i) => {
    const qIdx = questions.findIndex(q => q === essayQuestions[i]);
    return essayScores[qIdx] !== undefined && essayScores[qIdx] !== "";
  });

  const studentName = submission.studentName || submission.name || "";
  const studentId = submission.studentId || "";
  const submittedAt = submission.submittedAt?.includes("T")
    ? new Date(submission.submittedAt).toLocaleString("th-TH")
    : submission.submittedAt;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 overflow-y-auto"
      style={{ backdropFilter: "blur(4px)", backgroundColor: "rgba(0,0,0,0.5)", fontFamily: "'Noto Sans Thai', sans-serif" }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mb-10">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl z-10">
          <div>
            <h2 className="font-semibold text-[#1A1B1F] text-lg" style={{ fontFamily: "'Prompt', sans-serif" }}>
              คำตอบของ — {studentName}
            </h2>
            <p className="text-xs text-[#5E636E]">
              รหัสนิสิต: {studentId} | ส่งเมื่อ: {submittedAt}
              {examData && <span className="ml-2 text-[#007CB7]">| ข้อสอบ: {examData.title}</span>}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"><X size={18} strokeWidth={1.5} /></button>
        </div>

        {/* Score Summary Banner */}
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-6 text-sm">
          <div><span className="text-[#5E636E]">คะแนนปรนัย: </span><span className="font-bold text-[#007CB7]">{submission.mcScore}/{mcQuestions.reduce((s, q) => s + q.score, 0)}</span></div>
          <div><span className="text-[#5E636E]">คะแนนอัตนัย: </span><span className="font-bold text-emerald-600">{essayTotal}/{maxEssayScore}</span></div>
          <div><span className="text-[#5E636E]">รวม: </span><span className="font-bold text-[#1A1B1F] text-base">{totalScore}/{totalMax}</span></div>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* MC Questions */}
          {mcQuestions.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[#5E636E] uppercase tracking-wide mb-3 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">ปรนัย</span>
                คำถามปรนัย ({mcQuestions.length} ข้อ)
              </h3>
              <div className="space-y-4">
                {mcQuestions.map((q, relIdx) => {
                  const qIdx = questions.indexOf(q);
                  const chosen = submission.answers?.[qIdx] || submission.answers?.[String(qIdx)];
                  const correctId = getCorrectId(q);
                  const isRight = chosen === correctId;
                  return (
                    <div key={q.id || relIdx} className={`rounded-lg p-4 border ${isRight ? "border-green-200 bg-green-50/50" : chosen ? "border-red-200 bg-red-50/50" : "border-gray-200"}`}>
                      <div className="flex items-start gap-2 mb-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded flex-shrink-0 ${isRight ? "bg-green-200 text-green-800" : chosen ? "bg-red-200 text-red-800" : "bg-gray-200 text-gray-600"}`}>
                          ข้อ {relIdx + 1} {isRight ? "✓" : chosen ? "✗" : "—"}
                        </span>
                        <p className="text-sm text-[#1A1B1F]">{q.text}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {q.options.map((opt, i) => {
                          const optId = getOptId(opt, i);
                          const optText = getOptText(opt);
                          return (
                            <div key={optId} className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${getOptionStyle(optId, correctId, chosen)}`}>
                              <span className="font-semibold text-xs text-gray-400 w-4">{optId.toUpperCase()}.</span>
                              <span className="flex-1 text-[#1A1B1F]">{optText}</span>
                              {optId === correctId && <span className="text-xs text-green-600 font-bold flex-shrink-0">✓</span>}
                              {optId === chosen && optId !== correctId && <span className="text-xs text-red-500 flex-shrink-0">เลือก</span>}
                            </div>
                          );
                        })}
                      </div>
                      {!chosen && <p className="text-xs text-amber-600 mt-2">ไม่ได้เลือกคำตอบ</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Essay Questions */}
          {essayQuestions.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[#5E636E] uppercase tracking-wide mb-3 flex items-center gap-2">
                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">อัตนัย</span>
                คำถามอัตนัย — กรอกคะแนน
              </h3>
              <div className="space-y-4">
                {essayQuestions.map((q, relIdx) => {
                  const qIdx = questions.indexOf(q);
                  const answer = submission.answers?.[qIdx] || submission.answers?.[String(qIdx)] || "(ไม่มีคำตอบ)";
                  const score = essayScores[qIdx];
                  return (
                    <div key={q.id || relIdx} className="border border-gray-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex-shrink-0">ข้อ {relIdx + 1}</span>
                        <p className="text-sm text-[#1A1B1F]">{q.text}</p>
                      </div>
                      <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                        <p className="text-xs font-medium text-[#5E636E] mb-1">คำตอบของนิสิต:</p>
                        <p className="text-sm text-[#1A1B1F] leading-relaxed whitespace-pre-wrap">{answer}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-[#1A1B1F] flex-shrink-0">คะแนน (เต็ม {q.score}):</label>
                        <input
                          data-testid={`essay-score-input-${relIdx}`}
                          type="number" min="0" max={q.score}
                          value={score !== undefined ? score : ""}
                          onChange={e => {
                            const v = Math.min(Math.max(0, Number(e.target.value)), q.score);
                            setEssayScores(prev => ({ ...prev, [qIdx]: isNaN(v) ? "" : v }));
                          }}
                          placeholder="0"
                          className={`w-20 border-2 rounded-md px-3 py-2 text-sm text-center font-semibold focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none ${score !== undefined && score !== "" ? "border-green-400 bg-green-50" : "border-gray-300"}`}
                        />
                        {score !== undefined && score !== "" && <span className="text-xs text-green-600 font-medium">{score}/{q.score}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex items-center justify-between">
          <div className="text-sm">
            {allEssayScored
              ? <span className="text-green-600 font-medium flex items-center gap-1"><CheckCircle size={14} strokeWidth={1.5} />ตรวจครบทุกข้ออัตนัยแล้ว</span>
              : <span className="text-amber-600">กรุณากรอกคะแนนอัตนัยให้ครบทุกข้อ</span>}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-[#5E636E] hover:bg-white transition-colors">ปิด</button>
            <button data-testid="save-essay-scores-btn" onClick={handleSave}
              className="flex items-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white px-5 py-2 rounded-md text-sm font-semibold transition-colors">
              <Save size={14} strokeWidth={1.5} />บันทึกคะแนน ({totalScore}/{totalMax})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Submissions Tab — API-backed with real-time polling ─── */
const SubmissionsTab = ({ myExams, forceSubmitInfo, onGrantOverride, teacherName }) => {
  const [subs, setSubs] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState(myExams[0]?.id || "");
  const [loading, setLoading] = useState(false);
  const [overrideDone, setOverrideDone] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [pendingOverrides, setPendingOverrides] = useState([]); // from backend
  const lastTimestampRef = useRef(null);
  const newNotifCountRef = useRef(0);

  // Fetch submissions when selectedExamId changes
  useEffect(() => {
    if (!selectedExamId) return;
    setLoading(true);
    api.getSubmissions(selectedExamId)
      .then(res => {
        setSubs(res.data);
        if (res.data.length > 0) lastTimestampRef.current = res.data[0].submittedAt;
      })
      .catch(err => console.error("Fetch subs failed:", err))
      .finally(() => setLoading(false));
  }, [selectedExamId]);

  // Real-time polling every 5 seconds
  useEffect(() => {
    if (!selectedExamId) return;
    const poll = async () => {
      try {
        const res = await api.getSubmissionCount(selectedExamId, lastTimestampRef.current);
        const { count, latestTimestamp } = res.data;
        if (lastTimestampRef.current && count > 0) {
          newNotifCountRef.current += count;
          toast.success(`มีนิสิตส่งข้อสอบใหม่ ${count} คน`, {
            description: "กรุณาตรวจสอบในตารางด้านล่าง",
            duration: 5000,
          });
          // Refresh submissions list
          const subsRes = await api.getSubmissions(selectedExamId);
          setSubs(subsRes.data);
          if (subsRes.data.length > 0) lastTimestampRef.current = subsRes.data[0].submittedAt;
        } else if (!lastTimestampRef.current && latestTimestamp) {
          lastTimestampRef.current = latestTimestamp;
        }
      } catch (err) { /* silent - network errors during poll */ }
    };
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [selectedExamId]);

  // Poll for pending override requests from backend (cross-device support)
  useEffect(() => {
    const pollOverrides = async () => {
      try {
        const res = await api.getPendingOverrides(teacherName);
        setPendingOverrides(res.data || []);
      } catch (err) { /* silent */ }
    };
    pollOverrides();
    const interval = setInterval(pollOverrides, 5000);
    return () => clearInterval(interval);
  }, [teacherName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOverride = () => { setOverrideDone(true); onGrantOverride && onGrantOverride(); };

  const handleGrantApiOverride = async (subId) => {
    try {
      await api.grantOverride(subId);
      setPendingOverrides(prev => prev.filter(s => s.id !== subId));
      toast.success("ปลดล็อคสำเร็จ — นิสิตสามารถทำข้อสอบต่อได้");
      // Also reload submissions to reflect the change
      if (selectedExamId) {
        const res = await api.getSubmissions(selectedExamId);
        setSubs(res.data);
      }
    } catch (err) {
      toast.error("ปลดล็อคไม่สำเร็จ");
    }
  };

  const handleSaveScores = async (submissionId, essayScores, totalScore) => {
    try {
      const updated = await api.updateEssayScores(submissionId, essayScores, totalScore);
      setSubs(prev => prev.map(s => s.id === submissionId ? updated.data : s));
      toast.success("บันทึกคะแนนสำเร็จ");
    } catch (err) {
      toast.error("บันทึกคะแนนไม่สำเร็จ");
      // Fallback: update locally
      setSubs(prev => prev.map(s => s.id === submissionId ? {
        ...s, essayScores, status: "ตรวจแล้ว",
        totalScore: (s.mcScore || 0) + Object.values(essayScores).reduce((sum, v) => sum + Number(v || 0), 0),
      } : s));
    }
  };

  const downloadCSV = () => {
    const headers = ["รหัสนิสิต", "ชื่อ-นามสกุล", "ชั้นปี", "เวลาส่ง", "คะแนนปรนัย", "คะแนนอัตนัย", "คะแนนรวม", "คะแนนเต็ม", "สถานะ"];
    const rows = subs.map(s => {
      const et = s.essayScores ? Object.values(s.essayScores).reduce((sum, v) => sum + Number(v || 0), 0) : 0;
      return [s.studentId || "", `"${s.studentName || s.name || ""}"`, `ปี ${s.studentYear || s.year || ""}`, s.submittedAt || "", s.mcScore || 0, et, s.totalScore ?? "-", 30, s.status || ""];
    });
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ผลการสอบ_${selectedExamId}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success("ดาวน์โหลด CSV สำเร็จ");
  };

  const downloadEssayDoc = () => {
    const selectedExam = myExams.find(e => e.id === selectedExamId);
    const essayQ = selectedExam?.questions?.filter(q => q.type === "อัตนัย") || [];
    let html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{font-family:'Tahoma',sans-serif;font-size:12pt;margin:2cm}h1{color:#007CB7}h2{margin-top:18pt;border-bottom:1px solid #E5E7EB;padding-bottom:4pt}h3{color:#5E636E;font-size:11pt}.ans{background:#F9FAFB;border:1px solid #E5E7EB;padding:8pt;margin:4pt 0;line-height:1.8}hr{border:0;border-top:1px solid #E5E7EB;margin:14pt 0}</style></head><body>`;
    html += `<h1>คำตอบอัตนัย — ${selectedExam?.title || selectedExamId}</h1><p>วันที่: ${new Date().toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" })}</p><hr>`;
    subs.forEach((s, idx) => {
      const et = s.essayScores ? Object.values(s.essayScores).reduce((sum, v) => sum + Number(v || 0), 0) : 0;
      html += `<h2>${idx + 1}. ${s.studentName || s.name || ""} (${s.studentId || ""}) — ปรนัย: ${s.mcScore || 0}/15 | อัตนัย: ${et}/15</h2>`;
      essayQ.forEach((q, i) => {
        const qIdx = selectedExam.questions.indexOf(q);
        html += `<h3>ข้อ ${i + 1}: ${q.text} [คะแนน: ${s.essayScores?.[qIdx] ?? "รอตรวจ"}/${q.score}]</h3>`;
        html += `<div class="ans">${((s.answers?.[qIdx] || s.answers?.[String(qIdx)] || "(ไม่มีคำตอบ)").toString()).replace(/\n/g, "<br>")}</div>`;
      });
      html += `<hr>`;
    });
    html += `</body></html>`;
    const blob = new Blob(["\uFEFF" + html], { type: "application/msword;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `คำตอบอัตนัย_${selectedExamId}.doc`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success("ดาวน์โหลด Word สำเร็จ");
  };

  const forceRow = forceSubmitInfo ? [{
    id: "forced", studentId: forceSubmitInfo.studentInfo.studentId,
    name: `${forceSubmitInfo.studentInfo.prefix}${forceSubmitInfo.studentInfo.firstName} ${forceSubmitInfo.studentInfo.lastName}`,
    studentName: `${forceSubmitInfo.studentInfo.prefix}${forceSubmitInfo.studentInfo.firstName} ${forceSubmitInfo.studentInfo.lastName}`,
    year: forceSubmitInfo.studentInfo.year, submittedAt: "อัตโนมัติ",
    mcScore: "—", essayScores: {}, totalScore: null, status: "ส่งอัตโนมัติ", isCheat: true,
  }] : [];
  const allRows = [...forceRow, ...subs];
  const graded = subs.filter(s => s.status === "ตรวจแล้ว").length;

  return (
    <div className="space-y-4">
      {selectedSub && (
        <StudentDetailModal
          submission={selectedSub}
          examData={myExams.find(e => e.id === selectedExamId)}
          onClose={() => setSelectedSub(null)}
          onSave={handleSaveScores}
        />
      )}

      {/* ── Prominent override alert from backend (cross-device) ── */}
      {pendingOverrides.length > 0 && (
        <div className="bg-red-50 border-2 border-red-500 rounded-xl p-5 space-y-3 fade-in-up">
          <div className="flex items-center gap-2">
            <AlertTriangle size={20} strokeWidth={1.5} className="text-red-600 flex-shrink-0 animate-pulse" />
            <h3 className="font-bold text-red-700 text-base" style={{ fontFamily: "'Prompt', sans-serif" }}>
              คำร้องขอแก้ไข — นิสิตถูกส่งข้อสอบอัตโนมัติ ({pendingOverrides.length} คำร้อง)
            </h3>
          </div>
          {pendingOverrides.map(s => (
            <div key={s.id} className="bg-white border border-red-200 rounded-lg p-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-[#1A1B1F] text-sm">{s.studentName || s.name} <span className="font-mono text-xs text-[#007CB7]">({s.studentId})</span></p>
                <p className="text-xs text-[#5E636E] mt-0.5">ข้อสอบ: {s.examTitle} | ส่งเมื่อ: {s.submittedAt?.includes("T") ? new Date(s.submittedAt).toLocaleTimeString("th-TH") : s.submittedAt}</p>
              </div>
              <button data-testid={`grant-override-api-${s.id}`} onClick={() => handleGrantApiOverride(s.id)}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg text-sm font-bold transition-colors flex-shrink-0 shadow-sm">
                <RefreshCw size={14} strokeWidth={2} />ปลดล็อค — อนุญาตทำต่อ
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Exam Selector */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4 flex-wrap">
        <label className="text-sm font-medium text-[#1A1B1F] flex-shrink-0">เลือกข้อสอบที่ต้องการดูผล:</label>
        <select data-testid="exam-filter-select" value={selectedExamId} onChange={e => setSelectedExamId(e.target.value)}
          className="flex-1 min-w-[200px] border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none">
          <option value="">-- เลือกข้อสอบ --</option>
          {myExams.map(e => (
            <option key={e.id} value={e.id}>{e.title} ({e.status})</option>
          ))}
        </select>
        <div className="flex items-center gap-1.5 text-xs text-[#5E636E]">
          <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
          อัปเดตทุก 5 วินาที
        </div>
      </div>

      {/* Header & Actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-[#1A1B1F]" style={{ fontFamily: "'Prompt', sans-serif" }}>
            {myExams.find(e => e.id === selectedExamId)?.title || "เลือกข้อสอบเพื่อดูผล"}
          </h3>
          <p className="text-sm text-[#5E636E] mt-0.5">
            {loading ? "กำลังโหลด..." : `ส่งแล้ว ${allRows.length} คน | ตรวจแล้ว ${graded}/${subs.length} คน`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { const s = subs.find(x => x.status !== "ตรวจแล้ว"); if (s) setSelectedSub(s); else toast.info("ตรวจครบทุกคนแล้ว"); }}
            className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-[#1A1B1F] px-3 py-2 rounded-md text-sm font-medium transition-colors">
            <Eye size={14} strokeWidth={1.5} />ตรวจอัตนัย
          </button>
          <button data-testid="download-csv-btn" onClick={downloadCSV}
            className="flex items-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
            <Download size={14} strokeWidth={1.5} />CSV
          </button>
          <button data-testid="download-essay-btn" onClick={downloadEssayDoc}
            className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-[#1A1B1F] px-4 py-2 rounded-md text-sm font-medium transition-colors">
            <Download size={14} strokeWidth={1.5} />Word อัตนัย
          </button>
        </div>
      </div>

      {/* Override Alert */}
      {forceSubmitInfo && !overrideDone && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-5 py-4 flex items-start gap-4">
          <AlertTriangle size={20} strokeWidth={1.5} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-amber-800 text-sm">คำร้องขอแก้ไข — นิสิตถูกส่งข้อสอบอัตโนมัติ</p>
            <p className="text-amber-700 text-sm mt-0.5"><strong>{forceSubmitInfo.studentInfo.prefix}{forceSubmitInfo.studentInfo.firstName} {forceSubmitInfo.studentInfo.lastName}</strong> (รหัส: {forceSubmitInfo.studentInfo.studentId})</p>
          </div>
          <button data-testid="grant-override-btn" onClick={handleOverride}
            className="flex items-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex-shrink-0">
            <RefreshCw size={14} strokeWidth={1.5} />ปลดล็อค
          </button>
        </div>
      )}
      {overrideDone && <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-md px-4 py-3 text-sm text-green-700"><CheckCircle size={16} strokeWidth={1.5} />ปลดล็อคแล้ว</div>}

      {/* Submissions Table */}
      {!selectedExamId ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-lg py-12 text-center text-[#8A92A3] text-sm">
          กรุณาเลือกข้อสอบที่ต้องการดูผล
        </div>
      ) : loading ? (
        <div className="bg-white border border-gray-200 rounded-lg py-12 text-center text-[#8A92A3] text-sm">กำลังโหลดข้อมูล...</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="submissions-table">
            <thead><tr className="bg-gray-50 text-[#5E636E] text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-semibold">รหัสนิสิต</th>
              <th className="text-left px-4 py-3 font-semibold">ชื่อ–นามสกุล</th>
              <th className="text-left px-4 py-3 font-semibold">ชั้นปี</th>
              <th className="text-left px-4 py-3 font-semibold">เวลาส่ง</th>
              <th className="text-left px-4 py-3 font-semibold">ปรนัย</th>
              <th className="text-left px-4 py-3 font-semibold">อัตนัย</th>
              <th className="text-left px-4 py-3 font-semibold">รวม</th>
              <th className="text-left px-4 py-3 font-semibold">สถานะ</th>
              <th className="text-left px-4 py-3 font-semibold">คำตอบ</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {allRows.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-[#8A92A3]">ยังไม่มีนิสิตส่งข้อสอบ</td></tr>
              ) : allRows.map((s) => {
                const et = s.essayScores ? Object.values(s.essayScores).reduce((sum, v) => sum + Number(v || 0), 0) : 0;
                const submittedDisplay = s.submittedAt && s.submittedAt.includes("T")
                  ? new Date(s.submittedAt).toLocaleTimeString("th-TH")
                  : s.submittedAt;
                return (
                  <tr key={s.id} className={`hover:bg-gray-50 ${s.isCheat ? "bg-amber-50" : ""}`}>
                    <td className="px-4 py-3 font-mono text-xs text-[#007CB7] font-semibold">{s.studentId}</td>
                    <td className="px-4 py-3 text-[#1A1B1F] font-medium">{s.studentName || s.name || ""}</td>
                    <td className="px-4 py-3 text-[#5E636E] text-xs">ปี {s.studentYear || s.year || ""}</td>
                    <td className="px-4 py-3 text-[#5E636E] font-mono text-xs">{submittedDisplay}</td>
                    <td className="px-4 py-3 font-medium text-[#007CB7]">{s.mcScore !== "—" ? `${s.mcScore}/15` : "—"}</td>
                    <td className="px-4 py-3 font-medium text-emerald-600">{et > 0 ? `${et}/15` : <span className="text-amber-500 text-xs">รอตรวจ</span>}</td>
                    <td className="px-4 py-3 font-bold">{s.totalScore !== null && s.totalScore !== undefined ? `${s.totalScore}/30` : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.status === "ตรวจแล้ว" ? "bg-green-100 text-green-700" : s.status === "ส่งอัตโนมัติ" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{s.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      {!s.isCheat && (
                        <button data-testid={`view-student-${s.id}`} onClick={() => setSelectedSub(s)}
                          className="flex items-center gap-1 text-xs text-[#007CB7] hover:bg-blue-50 px-2 py-1 rounded">
                          <Eye size={12} strokeWidth={1.5} />ดูคำตอบ
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


/* ─── Main Teacher View ─── */
export default function TeacherView({ teachers, courses, setCourses, teacherLoggedIn, onLogin, onLogout, forceSubmitInfo, onGrantOverride, onChangePassword, savedExams, setSavedExams }) {
  const [activeTab, setActiveTab] = useState("create");
  const [examForm, setExamForm] = useState({ title: "", course: "", duration: 60, status: "เปิดสอบ", shuffleQuestions: false, shuffleOptions: false });
  const [questions, setQuestions] = useState([QUESTION_TEMPLATE()]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showImporter, setShowImporter] = useState(false);

  if (!teacherLoggedIn) return <TeacherLoginScreen teachers={teachers} onLogin={onLogin} />;

  // Only show this teacher's exams
  const myExams = savedExams.filter(e => e.teacherName === teacherLoggedIn.name);
  const myCourses = courses.filter(c => c.teacher === teacherLoggedIn.name);
  const handleFormChange = (f, v) => setExamForm(p => ({ ...p, [f]: v }));
  const addQuestion = () => setQuestions(p => [...p, QUESTION_TEMPLATE()]);
  const removeQuestion = (id) => { if (questions.length === 1) return; setQuestions(p => p.filter(q => q.id !== id)); };
  const updateQuestion = (id, f, v) => setQuestions(p => p.map(q => q.id === id ? { ...q, [f]: v } : q));
  const totalScore = questions.reduce((s, q) => s + Number(q.score || 0), 0);

  const handleSave = async () => {
    if (!examForm.title.trim()) { alert("กรุณากรอกชื่อการสอบก่อนบันทึก"); return; }
    if (!examForm.course) { alert("กรุณาเลือกรายวิชาก่อนบันทึก"); return; }
    const courseName = myCourses.find(c => c.id === examForm.course)?.name || examForm.course;

    // Normalize question options: convert string arrays → {id, text, isCorrect} objects
    const normalizeQuestion = (q) => ({
      ...q,
      options: q.type === "ปรนัย"
        ? q.options.map((opt, i) => ({
            id: String.fromCharCode(97 + i), // a, b, c, d
            text: typeof opt === "string" ? opt : (opt?.text || ""),
            isCorrect: q.correctOption === i,
          }))
        : [],
    });

    const examData = {
      ...examForm, courseName,
      questions: questions.map(normalizeQuestion),
      totalScore,
      teacherName: teacherLoggedIn.name,
    };
    try {
      const res = await api.createExam(examData);
      setSavedExams(prev => [res.data, ...prev]);
      setSaveSuccess(true);
      toast.success("บันทึกข้อสอบสำเร็จ", { description: `"${examData.title}" ถูกเผยแพร่แล้ว` });
      setTimeout(() => { setSaveSuccess(false); setActiveTab("myexams"); }, 1500);
    } catch (err) {
      toast.error("บันทึกไม่สำเร็จ", { description: err.response?.data?.detail || err.message });
    }
  };

  const STATUS_COLORS = {
    "เปิดสอบ": "bg-green-100 text-green-700 border-green-300",
    "ปิดชั่วคราว": "bg-amber-100 text-amber-700 border-amber-300",
    "ปิดสอบ": "bg-gray-100 text-gray-500 border-gray-300",
  };

  const setExamStatus = async (examId, newStatus) => {
    try {
      await api.updateExam(examId, { status: newStatus });
      setSavedExams(prev => prev.map(e => e.id === examId ? { ...e, status: newStatus } : e));
    } catch (err) {
      toast.error("อัปเดตสถานะไม่สำเร็จ");
    }
  };

  const deleteExam = async (id) => {
    try {
      await api.deleteExam(id);
      setSavedExams(prev => prev.filter(e => e.id !== id));
      toast.success("ลบข้อสอบสำเร็จ");
    } catch (err) {
      toast.error("ลบไม่สำเร็จ");
    }
  };

  const handleImport = (imported) => {
    setQuestions(prev => [...prev.filter(q => q.text), ...imported]);
    setSaveSuccess(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" style={{ fontFamily: "'Noto Sans Thai', sans-serif" }}>
      {showPasswordModal && <ChangePasswordModal teacher={teacherLoggedIn} onSave={onChangePassword} onClose={() => setShowPasswordModal(false)} />}
      {showImporter && <ExamImporter onImport={handleImport} onClose={() => setShowImporter(false)} />}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-[#1A1B1F]" style={{ fontFamily: "'Prompt', sans-serif" }}>ระบบอาจารย์ผู้สอน</h1>
          <p className="text-[#5E636E] text-sm mt-1 flex items-center gap-2"><UserCircle size={14} strokeWidth={1.5} />ยินดีต้อนรับ, <strong>{teacherLoggedIn.name}</strong></p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="change-password-btn" onClick={() => setShowPasswordModal(true)} className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-[#5E636E] px-3 py-2 rounded-md text-sm font-medium transition-colors">
            <Key size={14} strokeWidth={1.5} />เปลี่ยนรหัสผ่าน
          </button>
          <button data-testid="teacher-logout-btn" onClick={onLogout} className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-[#5E636E] px-3 py-2 rounded-md text-sm font-medium transition-colors">
            <LogOut size={14} strokeWidth={1.5} />ออกจากระบบ
          </button>
        </div>
      </div>

      <div className="mb-6"><MyCourseManager courses={courses} setCourses={setCourses} teacher={teacherLoggedIn} /></div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-6">
        <button data-testid="teacher-tab-create" onClick={() => setActiveTab("create")} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === "create" ? "border-[#007CB7] text-[#007CB7]" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
          <FileText size={15} strokeWidth={1.5} />สร้างข้อสอบออนไลน์
        </button>
        <button data-testid="teacher-tab-myexams" onClick={() => setActiveTab("myexams")} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === "myexams" ? "border-[#007CB7] text-[#007CB7]" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
          <BookOpen size={15} strokeWidth={1.5} />ข้อสอบของฉัน
          {myExams.length > 0 && <span className="bg-[#007CB7] text-white text-xs px-1.5 py-0.5 rounded-full">{myExams.length}</span>}
        </button>
        <button data-testid="teacher-tab-results" onClick={() => setActiveTab("results")} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === "results" ? "border-[#007CB7] text-[#007CB7]" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
          <Eye size={15} strokeWidth={1.5} />ดูผลการสอบ
          {forceSubmitInfo && <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full animate-pulse">คำร้อง!</span>}
        </button>
      </div>

      {activeTab === "create" && (
        <div className="space-y-6">
          {saveSuccess && <div data-testid="save-success-alert" className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-md px-4 py-3 text-sm fade-in-up"><CheckCircle size={16} strokeWidth={1.5} />บันทึกและเผยแพร่ข้อสอบสำเร็จ</div>}

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-5">
            <h2 className="font-semibold text-[#1A1B1F] text-base flex items-center gap-2" style={{ fontFamily: "'Prompt', sans-serif" }}>
              <BookOpen size={16} strokeWidth={1.5} className="text-[#007CB7]" />ตั้งค่าข้อสอบ
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">ชื่อการสอบ *</label><input data-testid="exam-title-input" type="text" value={examForm.title} onChange={(e) => handleFormChange("title", e.target.value)} placeholder="เช่น สอบกลางภาค: ทฤษฎีการสื่อสารมวลชน" className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" /></div>
              <div><label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">รายวิชา *</label>
                <select data-testid="exam-course-select" value={examForm.course} onChange={(e) => handleFormChange("course", e.target.value)} className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none">
                  <option value="">-- เลือกรายวิชา --</option>
                  {myCourses.map(c => <option key={c.id} value={c.id}>{c.id} — {c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">เวลาสอบ (นาที)</label><input data-testid="exam-duration-input" type="number" min="10" max="300" value={examForm.duration} onChange={(e) => handleFormChange("duration", Number(e.target.value))} className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" /></div>
              <div className="flex flex-col justify-end">
                <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-2.5 text-center">
                  <p className="text-xs text-[#5E636E]">คะแนนรวม</p>
                  <p className="text-2xl font-bold text-[#007CB7]" style={{ fontFamily: "'Prompt', sans-serif" }}>{totalScore}</p>
                  <p className="text-xs text-[#5E636E]">{questions.length} ข้อ</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-gray-100">
              {/* Status selector with 3 states */}
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm font-medium text-[#1A1B1F] mb-2">สถานะการสอบ</p>
                <div className="flex gap-1">
                  {["เปิดสอบ", "ปิดชั่วคราว", "ปิดสอบ"].map(s => (
                    <button key={s} onClick={() => handleFormChange("status", s)}
                      className={`flex-1 text-xs py-1.5 rounded border font-medium transition-all ${examForm.status === s ? STATUS_COLORS[s] : "bg-white border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              {[["toggle-shuffle-questions", "สุ่มคำถาม", "สับเปลี่ยนลำดับ", "shuffleQuestions"],
                ["toggle-shuffle-options", "สุ่มตัวเลือก", "สับเปลี่ยนตัวเลือก", "shuffleOptions"]
              ].map(([tid, label, sub, field]) => (
                <div key={tid} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div><p className="text-sm font-medium text-[#1A1B1F]">{label}</p><p className="text-xs text-[#5E636E]">{sub}</p></div>
                  <Switch data-testid={tid} checked={examForm[field]} onCheckedChange={(v) => handleFormChange(field, v)} />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-[#1A1B1F] text-base flex items-center gap-2" style={{ fontFamily: "'Prompt', sans-serif" }}>
                <FileText size={16} strokeWidth={1.5} className="text-[#007CB7]" />คำถามในข้อสอบ ({questions.length} ข้อ)
              </h2>
              <button data-testid="import-exam-btn" onClick={() => setShowImporter(true)} className="flex items-center gap-2 border border-[#007CB7] text-[#007CB7] hover:bg-blue-50 px-3 py-1.5 rounded-md text-xs font-medium transition-colors">
                <Upload size={13} strokeWidth={1.5} />นำเข้าจากไฟล์
              </button>
            </div>
            {questions.map((q, idx) => <QuestionCard key={q.id} question={q} index={idx} onChange={updateQuestion} onRemove={removeQuestion} />)}
            <button data-testid="add-question-btn" onClick={addQuestion} className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-[#5E636E] hover:border-[#007CB7] hover:text-[#007CB7] hover:bg-blue-50 transition-all flex items-center justify-center gap-2">
              <Plus size={16} strokeWidth={1.5} />เพิ่มคำถามใหม่
            </button>
          </div>

          <div className="flex justify-end pt-2">
            <button data-testid="save-exam-btn" onClick={handleSave} className="flex items-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white px-6 py-2.5 rounded-md font-medium transition-colors shadow-sm">
              <Save size={15} strokeWidth={1.5} />บันทึกและเผยแพร่ข้อสอบ
            </button>
          </div>
        </div>
      )}

      {activeTab === "results" && <SubmissionsTab myExams={myExams} forceSubmitInfo={forceSubmitInfo} onGrantOverride={onGrantOverride} teacherName={teacherLoggedIn.name} />}

      {/* ─── My Exams Tab ─── */}
      {activeTab === "myexams" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-[#1A1B1F] text-base" style={{ fontFamily: "'Prompt', sans-serif" }}>
              ข้อสอบของฉัน ({myExams.length} ข้อสอบ)
            </h2>
            <button onClick={() => { setActiveTab("create"); setSaveSuccess(false); }}
              className="flex items-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
              <Plus size={14} strokeWidth={1.5} />สร้างข้อสอบใหม่
            </button>
          </div>

          {myExams.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-lg py-14 text-center">
              <FileText size={36} strokeWidth={1} className="text-gray-300 mx-auto mb-3" />
              <p className="text-[#8A92A3] text-sm">ยังไม่มีข้อสอบที่บันทึก</p>
              <p className="text-xs text-[#8A92A3] mt-1">กรุณาสร้างข้อสอบในแท็บ "สร้างข้อสอบออนไลน์" แล้วกดบันทึก</p>
            </div>
          ) : (
            myExams.map(exam => (
              <div key={exam.id} data-testid={`saved-exam-${exam.id}`} className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold border ${STATUS_COLORS[exam.status] || "bg-gray-100 text-gray-500"}`}>
                        {exam.status || "เปิดสอบ"}
                      </span>
                      <span className="text-xs text-[#8A92A3]">บันทึกเมื่อ: {exam.createdAt}</span>
                    </div>
                    <h3 className="font-semibold text-[#1A1B1F] text-base" style={{ fontFamily: "'Prompt', sans-serif" }}>{exam.title}</h3>
                    <p className="text-sm text-[#5E636E] mt-0.5">
                      {exam.courseName || exam.course} | {exam.questions?.length} ข้อ | {exam.totalScore} คะแนน | {exam.duration} นาที
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {["เปิดสอบ", "ปิดชั่วคราว", "ปิดสอบ"].map(s => (
                      <button key={s} onClick={() => setExamStatus(exam.id, s)}
                        className={`text-xs px-2 py-1.5 rounded border font-medium transition-all ${exam.status === s ? STATUS_COLORS[s] : "bg-white border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                        {s}
                      </button>
                    ))}
                    <button onClick={() => deleteExam(exam.id)} className="ml-1 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded border border-red-200 transition-colors">
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap pt-2 border-t border-gray-100">
                  {exam.questions?.filter(q => q.type === "ปรนัย").length > 0 && (
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">ปรนัย {exam.questions.filter(q => q.type === "ปรนัย").length} ข้อ</span>
                  )}
                  {exam.questions?.filter(q => q.type === "อัตนัย").length > 0 && (
                    <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">อัตนัย {exam.questions.filter(q => q.type === "อัตนัย").length} ข้อ</span>
                  )}
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{exam.shuffleQuestions ? "สุ่มคำถาม ✓" : "ไม่สุ่มคำถาม"}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
